import { internalLogger } from '../../../interface/v1/clientOsLogger';
import { ValueStoreFacade } from '../../JWeb/facades';
import {
  ValueStoreGetResult,
  ValueStoreError,
  ValueStoreGetSuccessResult
} from '../../JWeb/types';
import type { ConsentResultType, ConsentValue } from '../strategies/types';
import { generateOptanonConsentString } from './cookieHelpers';

/**
 * ValueStore consent key mappings.
 *
 * Based on:  https://github.azc.ext.hp.com/ClientOS/consents-northbound-api/blob/main/src/constants.ts
 */
const VALUE_STORE_CONSENT_KEYS = {
  BASIC_FUNCTIONALITY: ['@hpx/consents/app/basic'],
  ANALYTICS: ['@hpx/consents/app/productImprovement'],
  ADVERTISING: ['@hpx/consents/app/advertising']
} as const;

/**
 * Service for mapping ValueStore consents to Optanon cookie format.
 * Handles the translation from native consent storage to web consent format.
 
  The possible values for each key are "Opted In", "Opted Out" and "No Selection".

  Type	            Description
  dataTransfer	    This consent allows the app to transfer telemetry data.
  basic	            This consent covers basic functionalities of the app.
  analytics/productImprovement	This consent allows the app to collect telemetry data for product improvement purposes.
  advertising	        This consent allows the app to collect telemetry data for advertising purposes.


  Maps consent keys according to specifications:
    Basic Functionality → Group 1 (always 1:1)
    Analytics → Group 2 (2:1 for opt-in, 2:0 for opt-out)
    Advertising → Groups 3 & 4 (3:1,4:1 for opt-in, 3:0,4:0 for opt-out)

    The first character represents a consent group and the second represents whether the user accepted/responded or not. For example:

    1:1 = user responded or closed the consents banner
    1:0 = user didn't respond the consents banner yet
    2:1 = User accepted the "Performance and Analytics" consent
    2:0 = User didn't accept the "Performance and Analytics" consent
 */
export class ValueStoreConsentMapper {
  /**
   * Retrieves consent values from ValueStore for specified keys
   * @param keys Array of ValueStore keys to retrieve
   * @returns Promise resolving to consent values with their states
   */
  private static async getConsentValuesFromValueStore(
    keys: readonly string[]
  ): Promise<ConsentValue[]> {
    try {
      const result = await ValueStoreFacade.get({ keys: [...keys] });

      if (this.isValueStoreError(result)) {
        internalLogger?.error(
          'ValueStore consent retrieval error:',
          result.errorReason
        );
        // Return all keys as not found when there's an error
        return keys.map((key) => ({
          key,
          value: false,
          found: false
        }));
      }

      // Process successful result - ValueStore result structure has a 'result' property with an array
      const successResult =
        result.result as unknown as ValueStoreGetSuccessResult;

      return keys.map((key) => {
        // ValueStoreGetSuccessResult is an array of entries
        const entry = Array.isArray(successResult)
          ? successResult.find((entry) => entry.key === key)
          : undefined;

        return {
          key,
          value: entry.value,
          found: !!entry
        };
      });
    } catch (error) {
      internalLogger?.error(
        'Error retrieving consent values from ValueStore:',
        error
      );
      return keys.map((key) => ({
        key,
        value: false,
        found: false
      }));
    }
  }

  /**
   * Type guard to check if ValueStore result is an error
   * TODO: move this to valueStoreFacade as a helper method
   */
  private static isValueStoreError(
    result: ValueStoreGetResult | ValueStoreError
  ): result is ValueStoreError {
    return 'errorType' in result;
  }

  /**
   * Determines consent status for a group based on ValueStore keys
   * @param keys Array of ValueStore keys to check
   * @returns Promise resolving to true if any key is consented to
   */
  public static async hasConsentForKeys(
    keys: readonly string[]
  ): Promise<boolean> {
    const consentValues = await this.getConsentValuesFromValueStore(keys);

    const isOptedIn = consentValues.some(
      (consent) => consent.found && consent.value === 'OptedIn'
    );
    /*
   // Not being used
    const isOptedOut = consentValues.some(
      (consent) => consent.found && consent.value === 'OptedOut'
    );
    */
    return isOptedIn;
  }

  /**
   * Lookup consent from ValueStore using direct key mapping
   */
  public static async lookupConsentFromValueStore(): Promise<ConsentResultType> {
    try {
      internalLogger?.debug?.(
        'NativeConsentStrategy: Looking up consents from ValueStore: ',
        VALUE_STORE_CONSENT_KEYS.BASIC_FUNCTIONALITY,
        VALUE_STORE_CONSENT_KEYS.ANALYTICS
      );

      // 1. Basic Functionality: Check if any basic functionality keys are true
      const basicFunctionalityConsent =
        await ValueStoreConsentMapper.hasConsentForKeys(
          VALUE_STORE_CONSENT_KEYS.BASIC_FUNCTIONALITY
        );

      // 2. Analytics: Check if any analytics keys are true
      const analyticsConsent = await ValueStoreConsentMapper.hasConsentForKeys(
        VALUE_STORE_CONSENT_KEYS.ANALYTICS
      );

      // 3. Advertising: Check if any advertising keys are true
      // For now, wont be used.
      /*const advertisingConsent =
        await ValueStoreConsentMapper.hasConsentForKeys(
          VALUE_STORE_CONSENT_KEYS.ADVERTISING
        );
        */
      const advertisingConsent = false;

      // Create Optanon groups based on ValueStore results
      const groupsObject: Record<string, 0 | 1> = {
        '1': basicFunctionalityConsent ? 1 : 0, // Basic Functionality
        '2': analyticsConsent ? 1 : 0, // Analytics
        '3': advertisingConsent ? 1 : 0, // Functional/Advertising
        '4': advertisingConsent ? 1 : 0 // Targeting/Advertising
      };

      const optanonConsent = generateOptanonConsentString({
        groups: groupsObject
      });

      internalLogger?.debug?.(
        'NativeConsentStrategy: Successfully mapped ValueStore consents to Optanon format',
        optanonConsent
      );

      return {
        optanonConsent,
        hasError: false
      };
    } catch (error) {
      internalLogger?.error(
        'NativeConsentStrategy: ValueStore lookup failed:',
        error
      );
      return {
        optanonConsent: '',
        hasError: true,
        errorMessage: `ValueStore lookup failed: ${error}`
      };
    }
  }
}
